import { consola } from 'consola';
import { isEmpty } from 'lodash-es';

interface DelegateCallbackResponse {
  logToConsole: boolean;
  context?: Record<string, unknown>;
}

export interface DelegatedLoggerOptions {
  beforeDebug?: (
    tag: string,
    message: string,
    context: Record<string, unknown>,
  ) => DelegateCallbackResponse;
  beforeInfo?: (
    tag: string,
    message: string,
    context: Record<string, unknown>,
  ) => DelegateCallbackResponse;
  beforeWarn?: (
    tag: string,
    message: string,
    context: Record<string, unknown>,
  ) => DelegateCallbackResponse;
  beforeError?: (
    tag: string,
    message: string,
    context: Record<string, unknown>,
  ) => DelegateCallbackResponse;
}

export class DelegatedLogger {
  private readonly logger = consola;

  private readonly beforeDebug?: DelegatedLoggerOptions['beforeDebug'];

  private readonly beforeInfo?: DelegatedLoggerOptions['beforeInfo'];

  private readonly beforeWarn?: DelegatedLoggerOptions['beforeWarn'];

  private readonly beforeError?: DelegatedLoggerOptions['beforeError'];

  public constructor({
    beforeDebug,
    beforeInfo,
    beforeWarn,
    beforeError,
  }: DelegatedLoggerOptions = {}) {
    this.beforeDebug = beforeDebug;
    this.beforeInfo = beforeInfo;
    this.beforeWarn = beforeWarn;
    this.beforeError = beforeError;
  }

  public debug(tag: string, message: string, context: Record<string, unknown> = {}) {
    const delegateResponse = this.beforeDebug?.(tag, message, context);

    if (!this.beforeDebug || delegateResponse?.logToConsole) {
      if (isEmpty(context) && isEmpty(delegateResponse?.context)) {
        this.logger.withTag(tag).debug(message);
      } else {
        this.logger.withTag(tag).debug(message, {
          ...(delegateResponse?.context ?? {}),
          ...context,
        });
      }
    }
  }

  public info(tag: string, message: string, context: Record<string, unknown> = {}) {
    const delegateResponse = this.beforeInfo?.(tag, message, context);

    if (!this.beforeInfo || delegateResponse?.logToConsole) {
      if (isEmpty(context) && isEmpty(delegateResponse?.context)) {
        this.logger.withTag(tag).info(message);
      } else {
        this.logger.withTag(tag).info(message, {
          ...(delegateResponse?.context ?? {}),
          ...context,
        });
      }
    }
  }

  public warn(tag: string, message: string, context: Record<string, unknown> = {}) {
    const delegateResponse = this.beforeWarn?.(tag, message, context);

    if (!this.beforeWarn || delegateResponse?.logToConsole) {
      if (isEmpty(context) && isEmpty(delegateResponse?.context)) {
        this.logger.withTag(tag).warn(message);
      } else {
        this.logger.withTag(tag).warn(message, {
          ...(delegateResponse?.context ?? {}),
          ...context,
        });
      }
    }
  }

  public error(tag: string, message: string, context: Record<string, unknown> = {}) {
    const delegateResponse = this.beforeError?.(tag, message, context);

    if (!this.beforeError || delegateResponse?.logToConsole) {
      if (isEmpty(context) && isEmpty(delegateResponse?.context)) {
        this.logger.withTag(tag).error(message);
      } else {
        this.logger.withTag(tag).error(message, {
          ...(delegateResponse?.context ?? {}),
          ...context,
        });
      }
    }
  }
}
